import React, { useState, useEffect } from "react";
import { EntityEditProvider, useEntityEdit } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { ShiftType } from "@/types/db/shifts";
import { UserType } from "@/types/db/users";
import { shiftsService } from "@/services/shiftService";
import { userService } from "@/services/userService";
import { listService, Position as ListPosition, Location as ListLocation } from "@/services/listService";
import { supabase } from "@/lib/supabase-client";

interface ShiftEditProviderProps extends Omit<EntityEditProviderProps<ShiftType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

interface ShiftEditContextType {
  availableUsers: UserType[];
  handleAssignUser: (userId: string, positionIndex: number) => Promise<void>;
  handleRemoveUser: (userId: string, positionIndex: number) => Promise<void>;
  handleInviteUsers: (userIds: string[], positionIndex: number) => Promise<void>;
  handleUpdatePosition: (positionIndex: number, updates: Partial<any>) => void;
  handleAssignSupervisor: (userId: string) => Promise<void>;
  handleRemoveSupervisor: (userId: string) => Promise<void>;
}

const ShiftEditContext = React.createContext<ShiftEditContextType | null>(null);

export function ShiftEditProvider({ children, entityData, loading, onSaveSuccess }: ShiftEditProviderProps) {
  const [availableUsers, setAvailableUsers] = useState<UserType[]>([]);
  const [positions, setPositions] = useState<ListPosition[]>([]);
  const [locations, setLocations] = useState<ListLocation[]>([]);

  // Helper function to enrich shift_positions with position and location data
  const enrichShiftPositions = React.useCallback(
    (shiftPositions: any[]) => {
      return shiftPositions.map((sp: any) => {
        const position = positions.find((p) => p.id === sp.position_id);
        const location = sp.location_id ? locations.find((l) => l.id === sp.location_id) : undefined;

        // Keep shift_users with their nested user data intact
        const enrichedShiftUsers = (sp.shift_users || []).map((su: any) => ({
          ...su,
          // Ensure user data is available for components
          user: su.user || {
            id: su.id,
            first_name: su.user?.first_name,
            last_name: su.user?.last_name,
            email_address: su.user?.email_address,
            preferred_name: su.user?.preferred_name,
          },
        }));

        return {
          ...sp,
          position: position ? { id: position.id, name: position.name, description: position.description } : undefined,
          location: location
            ? {
                id: location.id,
                name: location.name,
                address: {
                  street: location.street,
                  city: location.city,
                  state: location.state,
                  zip: location.zip,
                },
              }
            : undefined,
          shift_users: enrichedShiftUsers,
        };
      });
    },
    [positions, locations],
  );

  // Transform entityData for edit mode: convert shift_positions to meta_data.positions format
  const transformedEntityData = React.useMemo((): ShiftType | undefined => {
    if (!entityData) return entityData ?? undefined;

    // Enrich shift_positions with position and location data
    const enrichedShiftPositions = enrichShiftPositions(entityData.shift_positions || []);

    // Enrich shift location
    const shiftLocation = entityData.location_id ? locations.find((l) => l.id === entityData.location_id) : undefined;

    // Enrich project location
    const projectLocation = entityData.project?.location_id
      ? locations.find((l) => l.id === entityData.project?.location_id)
      : undefined;

    // Transform shift_positions (relational) to meta_data.positions (legacy format for UI)
    const transformedPositions = enrichedShiftPositions.map((sp: any) => ({
      shift_position_id: sp.id, // Store original ID for updates
      position_id: sp.position_id,
      name: sp.position?.name,
      role: sp.position?.name,
      description: sp.position?.description,
      quantity: sp.quantity || 1,
      supervisory: sp.supervisor,
      payroll_rate: sp.payroll_rate,
      gifted_hours: sp.gifted_hours,
      invited_users: (sp.shift_users || []).map((su: any) => ({
        id: su.id,
        status: su.status,
        meta_data: su.meta_data,
      })),
      location_override: sp.location_id ? { location: sp.location } : undefined,
      location: sp.location,
      meta_data: sp.meta_data,
    }));

    return {
      ...entityData,
      location: shiftLocation
        ? {
            id: shiftLocation.id,
            name: shiftLocation.name,
            address: {
              street: shiftLocation.street,
              city: shiftLocation.city,
              state: shiftLocation.state,
              zip: shiftLocation.zip,
            },
          }
        : undefined,
      project: entityData.project
        ? {
            ...entityData.project,
            location: projectLocation
              ? {
                  id: projectLocation.id,
                  name: projectLocation.name,
                  address: {
                    street: projectLocation.street,
                    city: projectLocation.city,
                    state: projectLocation.state,
                    zip: projectLocation.zip,
                  },
                }
              : undefined,
          }
        : undefined,
      shift_positions: enrichedShiftPositions, // Use enriched data
      meta_data: {
        ...entityData.meta_data,
        positions: transformedPositions,
      },
    };
  }, [entityData, enrichShiftPositions, locations]);

  // Load additional shift-specific data
  useEffect(() => {
    const fetchShiftData = async () => {
      try {
        const [userData, positionsData, locationsData] = await Promise.all([
          userService.getUsers(),
          listService.getPositions(),
          listService.getLocations(),
        ]);

        // Filter to ACTIVE users only for shift assignment
        const activeUsers = userData?.filter((u) => u.status === "ACTIVE") || [];
        setAvailableUsers(activeUsers);
        setPositions(positionsData || []);
        setLocations(locationsData || []);
      } catch (error) {
        console.error("Error fetching shift data:", error);
      }
    };

    fetchShiftData();
  }, []);

  const handleSave = async (formData: Partial<ShiftType>): Promise<ShiftType> => {
    if (!entityData?.id) {
      throw new Error("No shift ID available");
    }

    // Extract relational data that needs to be saved to separate tables
    // Also remove enriched fields that aren't database columns
    const {
      project,
      location,
      dress_code,
      shift_positions,
      shift_abilities,
      shift_restrictions,
      shift_supervisors,
      meta_data,
      onsite_contact_details: _onsite_contact_details,
      ...cleanFormData
    } = formData;

    // If project object exists, extract the IDs for the database
    if (project) {
      cleanFormData.project_id = project.id || formData.project_id;
      if (project.client_id) {
        cleanFormData.client_id = project.client_id;
      } else if (project.client?.id) {
        cleanFormData.client_id = project.client.id;
      }
    }

    // Keep non-position meta_data (remove positions)
    const { positions: _removedPositions, ...restMetaData } = meta_data || {};
    // Update shift base data (meta_data with positions stripped back on)
    const updatePayload: Partial<ShiftType> = { ...cleanFormData, meta_data: restMetaData };
    const updatedShift = await shiftsService.updateShift(entityData.id, updatePayload);
    if (!updatedShift) {
      throw new Error("Failed to update shift");
    }

    // Only sync positions if they were actually modified (not just from auto-transformation)
    // Check if meta_data.positions is different from the original entityData
    const positionsWereModified =
      meta_data?.positions &&
      (meta_data.positions !== transformedEntityData?.meta_data?.positions ||
        JSON.stringify(meta_data.positions) !== JSON.stringify(transformedEntityData?.meta_data?.positions));

    // Sync shift_positions and shift_users from meta_data.positions
    if (positionsWereModified) {
      // Build a map of current positions by ID for quick lookup
      const existingPositionsMap = new Map((entityData.shift_positions || []).map((p) => [p.id, p]));

      const processedIds = new Set<string>();

      // Process each position from formData
      for (const pos of meta_data.positions || []) {
        if (!pos.position_id) continue;

        if (pos.shift_position_id) {
          // UPDATE existing position
          await shiftsService.updateShiftPosition(pos.shift_position_id, {
            position_id: pos.position_id,
            location_id: pos.location_override?.location?.id || null,
            quantity: pos.quantity,
            supervisor: pos.supervisory,
            payroll_rate: pos.payroll_rate,
            gifted_hours: pos.gifted_hours,
            meta_data: pos.meta_data,
          });

          // Sync shift_users for this position
          const existingPosition = existingPositionsMap.get(pos.shift_position_id);
          const existingUsers = existingPosition?.shift_users || [];
          const newUsers = pos.invited_users || [];

          // Create sets for comparison
          const existingUserIds = new Set(existingUsers.map((u) => u.id));
          const newUserIds = new Set(newUsers.map((u) => u.id));

          // Add new users
          for (const user of newUsers) {
            if (!existingUserIds.has(user.id)) {
              await shiftsService.addShiftUser(pos.shift_position_id, user.id, user.status);
            } else {
              // Update status if changed
              const existing = existingUsers.find((u) => u.id === user.id);
              if (existing && existing.status !== user.status) {
                await shiftsService.updateShiftUserStatus(pos.shift_position_id, user.id, user.status);
              }
            }
          }

          // Remove users that are no longer assigned
          for (const existingUser of existingUsers) {
            if (!newUserIds.has(existingUser.id)) {
              await shiftsService.removeShiftUser(pos.shift_position_id, existingUser.id);
            }
          }

          processedIds.add(pos.shift_position_id);
        } else {
          // CREATE new position
          const newPosition = await shiftsService.createShiftPosition(
            entityData.id,
            pos.position_id,
            pos.location_override?.location?.id || undefined,
            pos.quantity,
            undefined, // preferredQuantity — not used here
            pos.supervisory,
            // fold the resolved pay figures into meta_data (no dedicated columns)
            { ...(pos.meta_data || {}), payroll_rate: pos.payroll_rate, gifted_hours: pos.gifted_hours },
          );

          // Add shift_users for new position
          if (pos.invited_users && newPosition.id) {
            for (const user of pos.invited_users) {
              await shiftsService.addShiftUser(newPosition.id, user.id, user.status);
            }
          }

          if (newPosition.id) {
            processedIds.add(newPosition.id);
          }
        }
      }

      // DELETE positions that were removed (not in formData)
      for (const existingPos of entityData.shift_positions || []) {
        if (existingPos.id && !processedIds.has(existingPos.id)) {
          await shiftsService.deleteShiftPosition(existingPos.id);
        }
      }
    }

    // Sync shift_supervisors if modified
    if (formData.shift_supervisors !== undefined) {
      const existingSupervisors = entityData.shift_supervisors || [];
      const newSupervisors = formData.shift_supervisors || [];

      // Create sets for comparison
      const existingUserIds = new Set(existingSupervisors.map((s: any) => s.user_id));
      const newUserIds = new Set(newSupervisors.map((s: any) => s.user_id));

      // Add new supervisors
      for (const supervisor of newSupervisors) {
        if (!existingUserIds.has(supervisor.user_id)) {
          await shiftsService.addShiftSupervisor(entityData.id, supervisor.user_id);
        }
      }

      // Remove supervisors that are no longer assigned
      for (const existing of existingSupervisors) {
        if (!newUserIds.has(existing.user_id)) {
          await shiftsService.removeShiftSupervisor(entityData.id, existing.user_id);
        }
      }
    }

    // Update link tables if they were modified
    if (shift_abilities !== undefined && entityData.tenant_id && entityData.client_id && entityData.project_id) {
      await shiftsService.syncShiftAbilities(
        entityData.id,
        entityData.tenant_id,
        entityData.client_id,
        entityData.project_id,
        shift_abilities || [],
      );
    }

    if (shift_restrictions !== undefined && entityData.tenant_id && entityData.client_id && entityData.project_id) {
      await shiftsService.syncShiftRestrictions(
        entityData.id,
        entityData.tenant_id,
        entityData.client_id,
        entityData.project_id,
        shift_restrictions || [],
      );
    }

    // Fetch the complete shift data with all joins to return
    const completeShift = await shiftsService.getShiftById(entityData.id);
    if (!completeShift) {
      throw new Error("Failed to fetch updated shift data");
    }

    return completeShift;
  };

  return (
    <EntityEditProvider
      entityData={transformedEntityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Shift"
    >
      <ShiftEditContextInner availableUsers={availableUsers}>{children}</ShiftEditContextInner>
    </EntityEditProvider>
  );
}

// Inner component that can access EntityEditContext
function ShiftEditContextInner({
  children,
  availableUsers,
}: {
  children: React.ReactNode;
  availableUsers: UserType[];
}) {
  const { formData, updateFormData } = useEntityEdit<ShiftType>();

  // Position management methods that update formData through context
  const handleAssignUser = async (userId: string, positionIndex: number) => {
    if (!formData.meta_data?.positions) return;

    const updatedPositions = [...formData.meta_data.positions];
    if (updatedPositions[positionIndex]) {
      const currentInvites = updatedPositions[positionIndex].invited_users || [];

      if (currentInvites.some((invite: any) => invite.id === userId)) {
        return;
      }

      updatedPositions[positionIndex] = {
        ...updatedPositions[positionIndex],
        invited_users: [...currentInvites, { id: userId, status: "QUEUED" }],
      };

      updateFormData({
        ...formData,
        meta_data: {
          ...formData.meta_data,
          positions: updatedPositions,
        },
      });
    }
  };

  const handleRemoveUser = async (userId: string, positionIndex: number) => {
    if (!formData.meta_data?.positions) return;

    const updatedPositions = [...formData.meta_data.positions];
    if (updatedPositions[positionIndex]) {
      const currentInvites = updatedPositions[positionIndex].invited_users || [];

      updatedPositions[positionIndex] = {
        ...updatedPositions[positionIndex],
        invited_users: currentInvites.filter((invite: any) => invite.id !== userId),
      };

      updateFormData({
        ...formData,
        meta_data: {
          ...formData.meta_data,
          positions: updatedPositions,
        },
      });
    }
  };

  const handleInviteUsers = async (userIds: string[], positionIndex: number) => {
    if (!formData.meta_data?.positions) return;

    const updatedPositions = [...formData.meta_data.positions];
    if (updatedPositions[positionIndex]) {
      const currentInvites = updatedPositions[positionIndex].invited_users || [];

      // Update status from QUEUED to INVITED for the specified users
      const updatedInvites = currentInvites.map((invite: any) => {
        if (userIds.includes(invite.id) && invite.status === "QUEUED") {
          return { ...invite, status: "INVITED" };
        }
        return invite;
      });

      updatedPositions[positionIndex] = {
        ...updatedPositions[positionIndex],
        invited_users: updatedInvites,
      };

      updateFormData({
        ...formData,
        meta_data: {
          ...formData.meta_data,
          positions: updatedPositions,
        },
      });
    }
  };

  const handleUpdatePosition = (positionIndex: number, updates: Partial<any>) => {
    if (!formData.meta_data?.positions) {
      console.log("No positions found in formData.meta_data");
      return;
    }

    const updatedPositions = [...formData.meta_data.positions];
    if (updatedPositions[positionIndex]) {
      const oldPosition = updatedPositions[positionIndex];
      updatedPositions[positionIndex] = {
        ...oldPosition,
        ...updates,
      };

      const newFormData = {
        ...formData,
        meta_data: {
          ...formData.meta_data,
          positions: updatedPositions,
        },
      };
      updateFormData(newFormData);
    } else {
    }
  };

  // Supervisor management methods
  const handleAssignSupervisor = async (userId: string) => {
    const currentSupervisors = formData.shift_supervisors || [];

    // Check if already assigned
    if (currentSupervisors.some((s: any) => s.user_id === userId)) {
      return;
    }

    // Add to shift_supervisors array
    updateFormData({
      ...formData,
      shift_supervisors: [
        ...currentSupervisors,
        {
          id: crypto.randomUUID(), // temporary client id until the save assigns the real one
          user_id: userId,
          permissions: {},
          meta_data: {},
        },
      ],
    });
  };

  const handleRemoveSupervisor = async (userId: string) => {
    const currentSupervisors = formData.shift_supervisors || [];

    // Remove from shift_supervisors array
    updateFormData({
      ...formData,
      shift_supervisors: currentSupervisors.filter((s: any) => s.user_id !== userId),
    });
  };

  const shiftEditContextValue: ShiftEditContextType = {
    availableUsers,
    handleAssignUser,
    handleRemoveUser,
    handleInviteUsers,
    handleUpdatePosition,
    handleAssignSupervisor,
    handleRemoveSupervisor,
  };

  return <ShiftEditContext.Provider value={shiftEditContextValue}>{children}</ShiftEditContext.Provider>;
}

export function useShiftEdit(): ShiftEditContextType {
  const context = React.useContext(ShiftEditContext);
  if (!context) {
    throw new Error("useShiftEdit must be used within a ShiftEditProvider");
  }
  return context;
}
